import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faEnvelope, faBell, faUsers } from '@/fontawesome.ts';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import Modal from '@/components/ui/Modal.tsx';
import type { RecommendationCreateStats } from '@/models';

interface RecommendationStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: RecommendationCreateStats;
  recommendationType: 'core' | 'earnings' | 'swing' | 'longterm';
  text: string;
}

const getTypeColor = (type: RecommendationStatsModalProps['recommendationType']): string => {
  switch (type) {
    case 'core':
      return 'text-blue-600';
    case 'earnings':
      return 'text-green-600';
    case 'swing':
    case 'longterm':
      return 'text-purple-600';
  }
};

export default function RecommendationStatsModal({
  isOpen,
  onClose,
  stats,
  recommendationType,
  text
}: RecommendationStatsModalProps) {
  const typeColor = getTypeColor(recommendationType);

  const statItems = [
    {
      icon: faUsers,
      label: 'Benutzer können dies sehen',
      value: stats.receivingUsers,
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: faDiscord,
      label: 'Discord-Nachrichten gesendet',
      value: stats.discordMessages,
      color: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      icon: faTelegram,
      label: 'Telegram-Nachrichten gesendet',
      value: stats.telegramMessages,
      color: 'text-sky-600 dark:text-sky-400'
    },
    {
      icon: faBell,
      label: 'SCN-Benachrichtigungen gesendet',
      value: stats.scnNotifications,
      color: 'text-amber-600 dark:text-amber-400'
    },
    {
      icon: faBell,
      label: 'Push-Benachrichtigungen (App) gesendet',
      value: stats.pushNotifications,
      color: 'text-purple-600 dark:text-purple-400'
    },
    {
      icon: faEnvelope,
      label: 'E-Mails gesendet',
      value: stats.mailNotifications,
      color: 'text-emerald-600 dark:text-emerald-400'
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Trades erfolgreich erstellt"
      icon={<FontAwesomeIcon icon={faCheckCircle} className={`w-5 h-5 ${typeColor}`} />}
      maxWidth="md"
    >
      <div className="p-6">
        <p className="text-gray-700 dark:text-gray-300 mb-6">{text}</p>

        <div className="space-y-3">
          {statItems.map((item, index) => (
            <div
              key={index}
              className={`flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 ${
                item.value === 0 ? 'opacity-50' : ''
              }`}
            >
              <FontAwesomeIcon icon={item.icon} className={`w-5 h-5 ${item.color} mr-3`} />
              <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{item.label}</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 font-medium transition-colors cursor-pointer"
          >
            Schließen
          </button>
        </div>
      </div>
    </Modal>
  );
}
